// =============================================================================
// components/WhatsAppShareDialog.js — WhatsApp ile Sipariş/Teklif Paylaş (Faz BL)
// =============================================================================
// Owner sipariş satırından "WhatsApp" butonuna basar →
//   - Sipariş özeti otomatik formatlanır (müşteri adı, items, tutar, takip linki)
//   - Düzenlenebilir textarea (kişiselleştir)
//   - "WhatsApp'ta Aç" → wa.me/<phone>?text=<encoded> ile yeni sekme/uygulama
//   - "Panoya Kopyala" → clipboard
// =============================================================================

import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Stack, Typography, CircularProgress, Alert, Box, IconButton, Tooltip
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import toast from "react-hot-toast";

const API_BASE = "https://api.miksanplastik.com.tr";

// Türk telefon → WhatsApp formatı (+90)
function waPhone(phone) {
  if (!phone) return null;
  const clean = String(phone).replace(/\D/g, '');
  if (clean.startsWith('0')) return '90' + clean.slice(1);
  if (clean.startsWith('5') && clean.length === 10) return '90' + clean;
  if (!clean.startsWith('90')) return '90' + clean;
  return clean;
}

// Sipariş objesinden WhatsApp mesajı şablonu üret
function buildOrderMessage(order) {
  const items = order.items || [];
  const fmt = (n) => Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const lines = [];
  lines.push(`Merhaba ${order.user_name || ''},`);
  lines.push('');
  lines.push(`Sipariş #${order.id} özeti:`);
  lines.push('');
  items.forEach(it => {
    lines.push(`• ${it.name || it.product_name} — ${it.quantity} ${it.unit || 'adet'} × ${fmt(it.unit_price)}₺ = ${fmt(it.quantity * it.unit_price)}₺`);
  });
  lines.push('');
  lines.push(`💰 TOPLAM: ${fmt(order.total_price)}₺`);
  if (order.status) {
    const statusTr = {
      pending: 'Onay bekliyor', paid_pending_approval: 'Ödeme alındı, onay bekliyor',
      confirmed: 'Onaylandı', preparing: 'Hazırlanıyor', shipped: 'Kargoda',
      delivered: 'Teslim edildi'
    }[order.status] || order.status;
    lines.push(`📦 Durum: ${statusTr}`);
  }
  lines.push('');
  lines.push('Mik-San Plastik · Tekirdağ · 0555 013 59 90');
  lines.push('miksanplastik.com.tr');
  return lines.join('\n');
}

export default function WhatsAppShareDialog({ orderId, open, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true); setError(null); setOrder(null); setMessage("");
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Sipariş yüklenemedi')))
      .then(o => {
        setOrder(o);
        setMessage(buildOrderMessage(o));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, orderId]);

  const phoneFormatted = order ? waPhone(order.user_phone) : null;
  const waUrl = phoneFormatted
    ? `https://wa.me/${phoneFormatted}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;  // numara yok → kullanıcı seçer

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Mesaj panoya kopyalandı');
    } catch (e) {
      toast.error('Kopyalanamadı: ' + e.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <span>WhatsApp ile Paylaş</span>
          {order && <Typography variant="caption" color="text.secondary">— Sipariş #{order.id}</Typography>}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {order && (
          <Stack spacing={2}>
            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, fontSize: 13 }}>
              <strong>Alıcı:</strong> {order.user_name} {order.user_company ? `/ ${order.user_company}` : ''}
              <br />
              <strong>Telefon:</strong> {order.user_phone || <span style={{ color: '#dc2626' }}>⚠️ Yok — kişi seçimi WhatsApp'ta yapılır</span>}
            </Box>

            <TextField
              multiline rows={12} fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              label="Mesaj (düzenleyebilirsin)"
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />

            <Alert severity="info" sx={{ py: 0 }}>
              💡 "WhatsApp'ta Aç" telefondan uygulamayı, masaüstünden web.whatsapp.com'u açar — gönder düğmesine sen basarsın.
            </Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
        <Tooltip title="Mesajı panoya kopyala">
          <Button onClick={copyToClipboard} startIcon={<ContentCopyIcon />} disabled={!order}>
            Panoya Kopyala
          </Button>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<WhatsAppIcon />}
          sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ea552' } }}
          component="a"
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!order}
        >
          {phoneFormatted ? "WhatsApp'ta Aç" : "WhatsApp'ı Aç (kişi seç)"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
